/**
 * /close/$periodId — Single close period workflow.
 *
 * Two-pane layout:
 *   - Left: status dashboard (progress %, days to target, blocking
 *     incomplete, overdue) — drives focus.
 *   - Right: checklist grouped by category, with per-item actions
 *     (start, complete, waive, edit).
 *
 * Auto-verify fires on every GET — items the system can confirm
 * itself (depreciation, manual JEs, ASC 606, payroll sync) get
 * auto-completed and show a "Auto-verified by system" note.
 *
 * Sign-off:
 *   - Disabled until status === pending_signoff and live blockers are clear.
 *   - Visible always so users see WHY it's disabled.
 *   - Hidden once status === closed (signed off). Lock is a separate live-control gate.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Snackbar,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import LockIcon from "@mui/icons-material/Lock";
import VerifiedIcon from "@mui/icons-material/VerifiedUser";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";

import { HelpHint } from "@/components/HelpHint";
import { api, errorMessage } from "@/lib/api";

// ─── Types ────────────────────────────────────────────────────────────────

type CloseItem = {
  id: string;
  close_period_id: string;
  template_id: string | null;
  name: string;
  category: string;
  description: string | null;
  owner_id: string | null;
  due_date: string | null;
  is_blocking: boolean;
  sort_order: number;
  status: "pending" | "in_progress" | "complete" | "waived";
  completion_note: string | null;
  completed_by: string | null;
  completed_at: string | null;
};

type ClosePeriod = {
  id: string;
  entity_id: string;
  fiscal_period_id: string;
  target_close_date: string;
  actual_close_date: string | null;
  status: "open" | "in_progress" | "pending_signoff" | "closed";
  opened_by: string;
  opened_at: string;
  signed_off_by: string | null;
  signed_off_at: string | null;
  sign_off_note: string | null;
  items_by_category: Record<string, CloseItem[]>;
};

type BriefingItemRef = {
  item_id: string;
  title: string;
  reason: string;
  owner_hint: string | null;
};

type WaivableRef = {
  item_id: string;
  title: string;
  reason: string;
};

type BlockedRef = {
  waiting_on_item_id: string;
  blocks_item_id: string;
  explanation: string;
};

type FluxRow = {
  account_id: string;
  account_name: string;
  account_code: string;
  account_type: string;
  current_period_cents: number;
  trailing_avg_cents: number;
  delta_cents: number;
  variance_pct: number | null;
  abs_delta_cents: number;
  direction: "up" | "down" | "in_pattern";
  severity: "info" | "warning" | "critical";
  explanation: string;
  action: string | null;
  top_journals: Array<{
    journal_id: string;
    journal_no: number;
    date: string;
    amount_cents: number;
    description: string;
    source: string;
  }>;
};

type AiFlux = {
  summary: string;
  rows: FluxRow[];
  generated_at: string;
  model: string;
};

type AiAccrualProposal = {
  id: string;
  source_type: "recurring_vendor";
  vendor_id: string;
  vendor_name: string;
  description: string;
  amount_cents: number;
  confidence: "high" | "medium" | "low";
  reasoning: string;
  debit_account_code: string | null;
  credit_account_code: string | null;
};

type AiAccrualReversal = {
  account_id: string;
  account_code: string;
  account_name: string;
  balance_cents: number;
  oldest_entry_days: number;
  suggestion: string;
};

type AiAccruals = {
  summary: string;
  proposed_accruals: AiAccrualProposal[];
  at_risk_reversals: AiAccrualReversal[];
  vendor_count_checked: number;
  accrual_account_count: number;
  generated_at: string;
  model: string;
};

type MemoSection = { title: string; body: string };

type AiNarrative = {
  memo: {
    period_label: string;
    period_end: string;
    close_status: string;
    headline: string;
    sections: MemoSection[];
    open_items: string[];
  };
  generated_at: string;
  model: string;
};

type AiBadDebtExposure = {
  customer_id: string;
  customer_name: string;
  total_outstanding_cents: number;
  current_cents: number;
  days_31_60_cents: number;
  days_61_90_cents: number;
  days_91_120_cents: number;
  days_120_plus_cents: number;
  invoice_count: number;
  oldest_dpd: number;
  recommended_reserve_cents: number;
  confidence: "high" | "medium" | "low";
  reasoning: string;
};

type AiBadDebtAgingSummary = {
  current_cents: number;
  days_31_60_cents: number;
  days_61_90_cents: number;
  days_91_120_cents: number;
  days_120_plus_cents: number;
};

type AiBadDebt = {
  summary: string;
  suggested_total_reserve_cents: number;
  customer_exposures: AiBadDebtExposure[];
  aging_summary: AiBadDebtAgingSummary;
  total_ar_cents: number;
  total_overdue_cents: number;
  customers_checked: number;
  generated_at: string;
  model: string;
};

type AiBriefing = {
  summary: string;
  headline_status: "on_track" | "at_risk" | "blocking" | "complete";
  days_to_target: number;
  critical_path: BriefingItemRef[];
  likely_waivable: WaivableRef[];
  blocked_downstream: BlockedRef[];
  pattern_insight: string | null;
  generated_at: string;
  model: string;
};

type CloseStatus = {
  close_period_id: string;
  fiscal_period_id: string;
  status: string;
  total_items: number;
  complete_items: number;
  waived_items: number;
  pending_items: number;
  overdue_items: number;
  blocking_incomplete: number;
  pct_complete: number;
  days_to_target: number | null;
  target_close_date: string;
};

type ControlStatus = "pass" | "break" | "unavailable" | "not_applicable";
type MoneyValue = string | number | null;

type ContinuousClose = {
  close_period_id: string;
  fiscal_period_id: string;
  period_start: string;
  period_end: string;
  generated_at: string;
  summary: {
    readiness: "ready" | "at_risk" | "blocked";
    blocker_count: number;
    tie_out_breaks: number;
    reconciliation_breaks: number;
    integration_failures: number;
    unavailable_controls: number;
    pending_posting_approvals: number;
    agent_journals: number;
    material_movements: number;
    control_overrides: number;
  };
  subledger_tie_outs: Array<{
    key: string;
    label: string;
    status: ControlStatus;
    subledger_balance: MoneyValue;
    gl_balance: MoneyValue;
    difference: MoneyValue;
    explanation: string;
    action_label: string;
    action_path: string;
  }>;
  reconciliations: Array<{
    bank_account_id: string;
    bank_account_name: string;
    gl_account_code: string;
    reconciliation_id: string | null;
    statement_date: string | null;
    reconciliation_status: string | null;
    status: ControlStatus;
    difference: MoneyValue;
    unmatched_lines: number;
    partial_lines: number;
    draft_groups: number;
    balanced_groups: number;
    explanation: string;
    action_label: string;
    action_path: string;
  }>;
  integration_failures: Array<{
    id: string;
    provider: string;
    integration_type: string;
    status: string;
    last_success_at: string | null;
    error: string;
    blocking: boolean;
    action_label: string;
    action_path: string;
  }>;
  agent_journals: Array<{
    journal_id: string;
    journal_no: number;
    journal_date: string;
    source: string;
    agent_name: string;
    description: string;
    status: string;
    amount: MoneyValue;
    approval_state: string | null;
    action_label: string;
    action_path: string;
  }>;
  pending_posting_approvals: Array<{
    approval_request_id: string;
    source_type: string;
    source_id: string;
    amount: MoneyValue;
    currency: string | null;
    requested_at: string;
    requested_by_name: string;
    current_approver_name: string | null;
    tier: number;
    control_status: string;
    control_blockers: string[];
    legacy_compatibility: boolean;
    policy_version: number | null;
    blocks_lock: boolean;
    action_label: string;
    action_path: string;
  }>;
  material_account_movements: Array<{
    account_id: string;
    account_code: string;
    account_name: string;
    account_type: string;
    current_period_amount: MoneyValue;
    trailing_average_amount: MoneyValue;
    difference: MoneyValue;
    variance_pct: number | null;
    severity: string;
    top_journal_id: string | null;
    top_journal_no: number | null;
    action_label: string;
    action_path: string;
  }>;
  control_overrides: Array<{
    id: string;
    control: string;
    override_type: string;
    reason: string;
    actor_id: string | null;
    actor_name: string;
    occurred_at: string;
    action_label: string;
    action_path: string;
  }>;
  lock_readiness: {
    status: "ready" | "not_ready" | "locked";
    fiscal_period_status: "open" | "closed" | "locked";
    signed_off: boolean;
    safe_to_lock: boolean;
    blocking_reasons: string[];
    explanation: string;
    action_label: string;
    action_path: string;
  };
  blockers: Array<{
    id: string;
    source: string;
    severity: string;
    title: string;
    detail: string;
    owner_id: string | null;
    due_date: string | null;
    action_label: string;
    action_path: string;
  }>;
};

// ─── Category presentation ────────────────────────────────────────────────

const CATEGORY_LABEL: Record<string, string> = {
  reconciliation: "Reconciliation",
  subledger: "Subledger tie-out",
  je_review: "Journal review",
  revenue: "Revenue",
  depreciation: "Depreciation",
  final: "Final review & sign-off",
};

const CATEGORY_ORDER = [
  "reconciliation",
  "subledger",
  "je_review",
  "revenue",
  "depreciation",
  "final",
];

// ─── Route ────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/_auth/close/$periodId")({
  component: ClosePeriodPage,
});

function ClosePeriodPage() {
  const { periodId } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [toast, setToast] = useState<{ msg: string; severity: "success" | "error" | "info" } | null>(null);
  const [signOffOpen, setSignOffOpen] = useState(false);
  const [adhocOpen, setAdhocOpen] = useState(false);
  const [waiveItem, setWaiveItem] = useState<CloseItem | null>(null);

  const periodQuery = useQuery({
    queryKey: ["close", "period", periodId],
    queryFn: () => api<ClosePeriod>(`/api/close/${periodId}`),
  });

  const statusQuery = useQuery({
    queryKey: ["close", "status", periodId],
    queryFn: () => api<CloseStatus>(`/api/close/${periodId}/status`),
    refetchInterval: 30_000,
  });

  const controlQuery = useQuery({
    queryKey: ["close", "control-center", periodId],
    queryFn: () => api<ContinuousClose>(`/api/close/${periodId}/control-center`),
    refetchInterval: 30_000,
  });

  const completeItem = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      api(`/api/close/items/${id}/complete`, {
        method: "POST",
        body: { note: note || null },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["close"] });
    },
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  const startItem = useMutation({
    mutationFn: (id: string) =>
      api(`/api/close/items/${id}/in-progress`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["close"] }),
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  const waiveItemMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api(`/api/close/items/${id}/waive`, {
        method: "POST",
        body: { reason },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["close"] });
      setWaiveItem(null);
      setToast({ msg: "Item waived", severity: "info" });
    },
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  const signOff = useMutation({
    mutationFn: (note: string) =>
      api(`/api/close/${periodId}/sign-off`, {
        method: "POST",
        body: { note: note || null },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["close"] });
      setSignOffOpen(false);
      setToast({ msg: "Period closed and signed off ✓", severity: "success" });
    },
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  const lockPeriod = useMutation({
    mutationFn: () => api(`/api/periods/${periodId}/lock`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["close"] });
      setToast({ msg: "Period locked — the accounting record is now audit-frozen ✓", severity: "success" });
    },
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  const briefing = useMutation({
    mutationFn: () => api<AiBriefing>(`/api/close/${periodId}/ai/briefing`, { method: "POST" }),
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  const flux = useMutation({
    mutationFn: () => api<AiFlux>(`/api/close/${periodId}/ai/flux`, { method: "POST" }),
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });
  const [fluxOpen, setFluxOpen] = useState(false);

  const accruals = useMutation({
    mutationFn: () => api<AiAccruals>(`/api/close/${periodId}/ai/accruals`, { method: "POST" }),
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });
  const [accrualsOpen, setAccrualsOpen] = useState(false);

  const badDebt = useMutation({
    mutationFn: () => api<AiBadDebt>(`/api/close/${periodId}/ai/bad-debt`, { method: "POST" }),
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });
  const [badDebtOpen, setBadDebtOpen] = useState(false);

  const narrative = useMutation({
    mutationFn: () => api<AiNarrative>(`/api/close/${periodId}/ai/narrative`, { method: "POST" }),
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });
  const [narrativeOpen, setNarrativeOpen] = useState(false);

  const addAdhoc = useMutation({
    mutationFn: (payload: { name: string; category: string; description: string; is_blocking: boolean }) =>
      api(`/api/close/${periodId}/items`, { method: "POST", body: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["close"] });
      setAdhocOpen(false);
      setToast({ msg: "Ad-hoc item added", severity: "success" });
    },
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  if (periodQuery.isLoading) {
    return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>;
  }
  if (periodQuery.error || !periodQuery.data) {
    return (
      <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
        <Alert severity="error">{errorMessage(periodQuery.error) || "Close period not found"}</Alert>
        <Button startIcon={<ArrowBackIcon />} component={Link} to={"/close" as never} sx={{ mt: 2 }}>
          Back to Close
        </Button>
      </Box>
    );
  }

  const period = periodQuery.data;
  const status = statusQuery.data;
  const isClosed = period.status === "closed";
  const liveControlsClear = controlQuery.data?.summary.blocker_count === 0;
  const canSignOff = period.status === "pending_signoff" && liveControlsClear;
  const isLocked = controlQuery.data?.lock_readiness?.status === "locked";
  const canLock = controlQuery.data?.lock_readiness?.safe_to_lock === true;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to={"/close" as never}
            sx={{ mb: 0.5 }}
          >
            Close calendar
          </Button>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            <Typography variant="h5">
              Continuous close · {period.target_close_date}
              {period.actual_close_date && (
                <Typography component="span" variant="body2" color="success.main" sx={{ ml: 1.5 }}>
                  · Signed off {period.actual_close_date}
                </Typography>
              )}
            </Typography>
            <HelpHint docPath="close/continuous-close.md" label="Control center guide" />
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          {!isClosed && (
            <Button
              startIcon={<AutoAwesomeIcon />}
              variant="outlined"
              onClick={() => {
                setFluxOpen(true);
                if (!flux.data) flux.mutate();
              }}
            >
              Flux analysis
            </Button>
          )}
          {!isClosed && (
            <Button
              startIcon={<AutoAwesomeIcon />}
              variant="outlined"
              onClick={() => {
                setAccrualsOpen(true);
                if (!accruals.data) accruals.mutate();
              }}
            >
              Accruals
            </Button>
          )}
          {!isClosed && (
            <Button
              startIcon={<AutoAwesomeIcon />}
              variant="outlined"
              onClick={() => {
                setBadDebtOpen(true);
                if (!badDebt.data) badDebt.mutate();
              }}
            >
              Bad Debt
            </Button>
          )}
          <Button
            startIcon={<AutoAwesomeIcon />}
            variant="outlined"
            onClick={() => {
              setNarrativeOpen(true);
              if (!narrative.data) narrative.mutate();
            }}
          >
            Memo
          </Button>
          {!isClosed && (
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() => setAdhocOpen(true)}
            >
              Add item
            </Button>
          )}
          {!isClosed && (
            <Tooltip
              title={
                canSignOff
                  ? "Sign off and close the fiscal period"
                  : period.status !== "pending_signoff"
                    ? "All blocking checklist items must be complete or waived first"
                    : controlQuery.isLoading
                      ? "Waiting for live close controls"
                      : `${controlQuery.data?.summary.blocker_count ?? "Live"} control blocker(s) must be resolved first`
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<LockIcon />}
                  disabled={!canSignOff}
                  onClick={() => setSignOffOpen(true)}
                >
                  Sign off & close
                </Button>
              </span>
            </Tooltip>
          )}
          {isClosed && !isLocked && (
            <Tooltip
              title={
                canLock
                  ? "Audit-freeze the signed-off period"
                  : controlQuery.data?.lock_readiness?.blocking_reasons.join(" ") || "Waiting for live lock controls"
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<LockIcon />}
                  disabled={!canLock || lockPeriod.isPending}
                  onClick={() => lockPeriod.mutate()}
                >
                  {lockPeriod.isPending ? "Locking…" : "Lock period"}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {isClosed && (
        <Alert severity={isLocked ? "success" : "info"} icon={isLocked ? <LockIcon /> : <VerifiedIcon />} sx={{ mb: 3 }}>
          {isLocked
            ? "This period is signed off and audit-locked."
            : "This period is signed off and closed. It remains unlocked until the live lock decision is clear."}{" "}
          {period.sign_off_note && `Note: ${period.sign_off_note}`}
        </Alert>
      )}

      <ContinuousCloseControlCenter
        data={controlQuery.data ?? null}
        isLoading={controlQuery.isLoading}
        error={controlQuery.error ? errorMessage(controlQuery.error) : null}
        isRefreshing={controlQuery.isFetching}
        onRefresh={() => controlQuery.refetch()}
      />

      {/* AI Briefing — manual run; refresh whenever the checklist changes */}
      {!isClosed && (
        <BriefingCard
          briefing={briefing.data ?? null}
          isPending={briefing.isPending}
          error={briefing.error ? errorMessage(briefing.error) : null}
          onRun={() => briefing.mutate()}
          period={period}
        />
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
        {/* Status pane */}
        <Box sx={{ minWidth: 280, maxWidth: { md: 320 } }}>
          <Card sx={{ position: { md: "sticky" }, top: { md: 16 } }}>
            <CardHeader title="Status" />
            <CardContent>
              {status && (
                <Stack spacing={2}>
                  <Box>
                    <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Progress</Typography>
                      <Typography variant="h6">{status.pct_complete.toFixed(0)}%</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={status.pct_complete}
                      sx={{ height: 8, borderRadius: 1, mt: 0.5 }}
                      color={status.pct_complete >= 100 ? "success" : status.pct_complete >= 60 ? "primary" : "warning"}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      {status.complete_items + status.waived_items} / {status.total_items} done
                    </Typography>
                  </Box>

                  <Stack spacing={1}>
                    <StatusRow label="Pending" value={status.pending_items} color="default" />
                    <StatusRow label="Complete" value={status.complete_items} color="success" />
                    <StatusRow label="Waived" value={status.waived_items} color="warning" />
                    <StatusRow label="Overdue" value={status.overdue_items} color="error" highlight />
                    <StatusRow label="Blocking incomplete" value={status.blocking_incomplete} color="error" highlight />
                  </Stack>

                  <Box sx={{ pt: 1, borderTop: 1, borderColor: "divider" }}>
                    <Typography variant="caption" color="text.secondary">Target close</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {status.target_close_date}
                    </Typography>
                    {status.days_to_target !== null && !isClosed && (
                      <Typography
                        variant="caption"
                        color={status.days_to_target < 0 ? "error.main" : status.days_to_target <= 2 ? "warning.main" : "text.secondary"}
                      >
                        {status.days_to_target < 0
                          ? `${Math.abs(status.days_to_target)} days overdue ⚠`
                          : status.days_to_target === 0
                            ? "Due today"
                            : `${status.days_to_target} days to target`}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Checklist pane */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack spacing={2}>
            {CATEGORY_ORDER.map((cat) => {
              const items = period.items_by_category[cat];
              if (!items || items.length === 0) return null;
              return (
                <CategoryCard
                  key={cat}
                  category={cat}
                  items={items}
                  isClosed={isClosed}
                  onComplete={(id, note) => completeItem.mutate({ id, note })}
                  onStart={(id) => startItem.mutate(id)}
                  onWaive={(item) => setWaiveItem(item)}
                />
              );
            })}
            {/* Any uncategorized items (defensive — shouldn't happen with seed data) */}
            {Object.entries(period.items_by_category)
              .filter(([cat]) => !CATEGORY_ORDER.includes(cat))
              .map(([cat, items]) => (
                <CategoryCard
                  key={cat}
                  category={cat}
                  items={items}
                  isClosed={isClosed}
                  onComplete={(id, note) => completeItem.mutate({ id, note })}
                  onStart={(id) => startItem.mutate(id)}
                  onWaive={(item) => setWaiveItem(item)}
                />
              ))}
          </Stack>
        </Box>
      </Stack>

      {/* Sign-off dialog */}
      <SignOffDialog
        open={signOffOpen}
        onClose={() => setSignOffOpen(false)}
        onConfirm={(note) => signOff.mutate(note)}
        isPending={signOff.isPending}
      />

      {/* Ad-hoc item dialog */}
      <AdhocDialog
        open={adhocOpen}
        onClose={() => setAdhocOpen(false)}
        onConfirm={(payload) => addAdhoc.mutate(payload)}
        isPending={addAdhoc.isPending}
      />

      {/* Waive dialog */}
      <WaiveDialog
        item={waiveItem}
        onClose={() => setWaiveItem(null)}
        onConfirm={(reason) => waiveItem && waiveItemMut.mutate({ id: waiveItem.id, reason })}
        isPending={waiveItemMut.isPending}
      />

      {/* AI Flux analysis dialog */}
      <FluxDialog
        open={fluxOpen}
        onClose={() => setFluxOpen(false)}
        data={flux.data ?? null}
        isPending={flux.isPending}
        error={flux.error ? errorMessage(flux.error) : null}
        onRefresh={() => flux.mutate()}
      />

      {/* AI Accrual drafter dialog */}
      <AccrualsDialog
        open={accrualsOpen}
        onClose={() => setAccrualsOpen(false)}
        data={accruals.data ?? null}
        isPending={accruals.isPending}
        error={accruals.error ? errorMessage(accruals.error) : null}
        onRefresh={() => accruals.mutate()}
      />

      {/* AI Bad-debt estimator dialog */}
      <BadDebtDialog
        open={badDebtOpen}
        onClose={() => setBadDebtOpen(false)}
        data={badDebt.data ?? null}
        isPending={badDebt.isPending}
        error={badDebt.error ? errorMessage(badDebt.error) : null}
        onRefresh={() => badDebt.mutate()}
      />

      {/* AI Close-package narrative dialog */}
      <NarrativeDialog
        open={narrativeOpen}
        onClose={() => setNarrativeOpen(false)}
        data={narrative.data ?? null}
        isPending={narrative.isPending}
        error={narrative.error ? errorMessage(narrative.error) : null}
        onRefresh={() => narrative.mutate()}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? <Alert severity={toast.severity}>{toast.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
}

// ─── Continuous close control center ─────────────────────────────────────

const controlColor = (status: string): "success" | "error" | "warning" | "default" =>
  status === "pass" || status === "ready" || status === "locked"
    ? "success"
    : status === "break" || status === "blocked" || status === "critical" || status === "not_ready"
      ? "error"
      : status === "at_risk" || status === "unavailable" || status === "warning"
        ? "warning"
        : "default";

const controlMoney = (value: MoneyValue) =>
  value === null || value === undefined
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(Number(value));

function ControlAction({ path, label }: { path: string; label: string }) {
  return (
    <Button
      component={Link}
      to={path as never}
      size="small"
      variant="text"
      sx={{ px: 0, justifyContent: "flex-start", minWidth: 0 }}
    >
      {label} →
    </Button>
  );
}

function ContinuousCloseControlCenter({
  data,
  isLoading,
  error,
  isRefreshing,
  onRefresh,
}: {
  data: ContinuousClose | null;
  isLoading: boolean;
  error: string | null;
  isRefreshing: boolean;
  onRefresh: () => void;
}) {
  if (isLoading && !data) {
    return (
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
            <Skeleton width={280} height={34} />
            <Skeleton width={90} height={30} />
          </Stack>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
            {[0, 1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={150} />)}
          </Box>
        </CardContent>
      </Card>
    );
  }
  if (error && !data) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 3 }}
        action={<Button color="inherit" size="small" onClick={onRefresh}>Retry</Button>}
      >
        Live close controls could not load: {error}
      </Alert>
    );
  }
  if (!data) return null;

  const lockReadiness = data.lock_readiness ?? {
    status: "not_ready" as const,
    fiscal_period_status: "open" as const,
    signed_off: false,
    safe_to_lock: false,
    blocking_reasons: ["The live lock decision is still loading."],
    explanation: "Refresh controls before locking the period.",
    action_label: "Open period controls",
    action_path: "/periods",
  };
  const postingApprovals = data.pending_posting_approvals ?? [];
  const agentJournals = data.agent_journals ?? [];
  const materialMovements = data.material_account_movements ?? [];
  const controlOverrides = data.control_overrides ?? [];

  const summaryStats = [
    ["Live blockers", data.summary.blocker_count, "error"],
    ["Tie-out breaks", data.summary.tie_out_breaks, "error"],
    ["Recon breaks", data.summary.reconciliation_breaks, "error"],
    ["Posting approvals", data.summary.pending_posting_approvals ?? 0, "error"],
    ["Integration failures", data.summary.integration_failures, "warning"],
    ["Agent journals", data.summary.agent_journals ?? 0, "primary"],
    ["Material movements", data.summary.material_movements ?? 0, "warning"],
    ["Control overrides", data.summary.control_overrides ?? 0, "warning"],
  ] as const;

  return (
    <Card
      sx={{
        mb: 3,
        borderRadius: 3,
        overflow: "hidden",
        border: 1,
        borderColor: data.summary.readiness === "ready" ? "success.light" : data.summary.readiness === "blocked" ? "error.light" : "warning.light",
        boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          color: "common.white",
          background: data.summary.readiness === "ready"
            ? "linear-gradient(120deg, #064e3b, #0f766e)"
            : data.summary.readiness === "blocked"
              ? "linear-gradient(120deg, #7f1d1d, #be123c)"
              : "linear-gradient(120deg, #78350f, #b45309)",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h6" fontWeight={700}>Continuous-close control center</Typography>
              <Chip
                size="small"
                label={data.summary.readiness.replace("_", " ")}
                sx={{ bgcolor: "rgba(255,255,255,.18)", color: "white", fontWeight: 700 }}
              />
            </Stack>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,.78)", mt: 0.25 }}>
              Live through {data.period_end} · refreshed {new Date(data.generated_at).toLocaleTimeString()}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={isRefreshing}
            sx={{ color: "white", borderColor: "rgba(255,255,255,.45)", "&:hover": { borderColor: "white" } }}
          >
            {isRefreshing ? "Refreshing…" : "Refresh controls"}
          </Button>
        </Stack>
      </Box>

      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>Showing the last successful snapshot. Refresh failed: {error}</Alert>
        )}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 1.5, mb: 2.5 }}>
          {summaryStats.map(([label, value, tone]) => (
            <Box key={label} sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover", border: 1, borderColor: "divider" }}>
              <Typography
                variant="h5"
                fontWeight={750}
                color={value ? `${tone}.main` : "success.main"}
              >
                {value}
              </Typography>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
          ))}
        </Box>

        <Alert
          severity={lockReadiness.status === "locked" || lockReadiness.safe_to_lock ? "success" : "warning"}
          icon={<LockIcon />}
          sx={{ mb: 2.5, alignItems: "flex-start" }}
          action={<ControlAction path={lockReadiness.action_path} label={lockReadiness.action_label} />}
        >
          <Typography variant="subtitle2" fontWeight={750}>
            {lockReadiness.status === "locked"
              ? "Period is audit-locked"
              : lockReadiness.safe_to_lock
                ? "Safe to lock now"
                : "Not safe to lock yet"}
          </Typography>
          <Typography variant="body2">{lockReadiness.explanation}</Typography>
          {lockReadiness.blocking_reasons.map((reason) => (
            <Typography key={reason} variant="caption" sx={{ display: "block" }}>• {reason}</Typography>
          ))}
        </Alert>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
          <ControlSection title="Subledger tie-outs" subtitle="Source detail compared with the posted control account">
            {data.subledger_tie_outs.map((tie) => (
              <ControlRow key={tie.key} title={tie.label} status={tie.status} detail={tie.explanation}>
                {(tie.status === "pass" || tie.status === "break") && (
                  <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mt: 0.75 }}>
                    <Typography variant="caption">Subledger <strong>{controlMoney(tie.subledger_balance)}</strong></Typography>
                    <Typography variant="caption">GL <strong>{controlMoney(tie.gl_balance)}</strong></Typography>
                    <Typography variant="caption" color={Number(tie.difference) ? "error.main" : "success.main"}>
                      Difference <strong>{controlMoney(tie.difference)}</strong>
                    </Typography>
                  </Stack>
                )}
                <ControlAction path={tie.action_path} label={tie.action_label} />
              </ControlRow>
            ))}
          </ControlSection>

          <ControlSection title="Bank & settlement reconciliations" subtitle="Period coverage, partial allocations, groups, and book-to-bank balance">
            {data.reconciliations.length === 0 ? (
              <ControlEmpty
                title="No active bank accounts"
                detail="Add the entity's operating accounts to make bank coverage a live close control."
                path="/bank-accounts"
                action="Set up bank accounts"
              />
            ) : data.reconciliations.map((rec) => (
              <ControlRow
                key={rec.bank_account_id}
                title={`${rec.bank_account_name} · ${rec.gl_account_code}`}
                status={rec.status}
                detail={rec.explanation}
              >
                <Stack direction="row" spacing={1.25} flexWrap="wrap" sx={{ mt: 0.75 }}>
                  {rec.statement_date && <Typography variant="caption">Statement {rec.statement_date}</Typography>}
                  <Typography variant="caption">Unmatched <strong>{rec.unmatched_lines}</strong></Typography>
                  <Typography variant="caption">Partial <strong>{rec.partial_lines}</strong></Typography>
                  <Typography variant="caption">Draft groups <strong>{rec.draft_groups}</strong></Typography>
                  <Typography variant="caption">Difference <strong>{controlMoney(rec.difference)}</strong></Typography>
                </Stack>
                <ControlAction path={rec.action_path} label={rec.action_label} />
              </ControlRow>
            ))}
          </ControlSection>

          <ControlSection title="Integration health" subtitle="Only failed or disconnected sources appear here">
            {data.integration_failures.length === 0 ? (
              <ControlEmpty
                title="No integration failures"
                detail="Connected bank, payroll, CRM, and vendor feeds report no current errors."
                path="/settings/integrations"
                action="Manage integrations"
                success
              />
            ) : data.integration_failures.map((failure) => (
              <ControlRow
                key={failure.id}
                title={`${failure.provider} · ${failure.integration_type.replace("_", " ")}`}
                status={failure.blocking ? "break" : "unavailable"}
                detail={failure.error}
              >
                <ControlAction path={failure.action_path} label={failure.action_label} />
              </ControlRow>
            ))}
          </ControlSection>

          <ControlSection title="Close blockers" subtitle="The live queue that gates sign-off">
            {data.blockers.length === 0 ? (
              <ControlEmpty
                title="No close blockers"
                detail="All live controls and blocking checklist items are clear."
                path="/close"
                action="Return to close calendar"
                success
              />
            ) : (
              <Box sx={{ maxHeight: 360, overflowY: "auto", pr: 0.5 }}>
                {data.blockers.map((blocker) => (
                  <ControlRow
                    key={blocker.id}
                    title={blocker.title}
                    status="break"
                    detail={blocker.detail}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" label={blocker.source} variant="outlined" sx={{ height: 20, fontSize: "0.68rem" }} />
                      {blocker.due_date && <Typography variant="caption" color="text.secondary">Due {blocker.due_date}</Typography>}
                    </Stack>
                    <ControlAction path={blocker.action_path} label={blocker.action_label} />
                  </ControlRow>
                ))}
              </Box>
            )}
          </ControlSection>

          <ControlSection title="Posting approvals" subtitle="Financial events that can still create or change a posting">
            {postingApprovals.length === 0 ? (
              <ControlEmpty
                title="No postings await approval"
                detail="The entity approval queue has no pending financial posting requests."
                path="/approvals"
                action="Open approval queue"
                success
              />
            ) : postingApprovals.map((approval) => (
              <ControlRow
                key={approval.approval_request_id}
                title={`${approval.source_type.replaceAll("_", " ")} · tier ${approval.tier}`}
                status={approval.blocks_lock ? "break" : "warning"}
                detail={approval.control_blockers.length
                  ? `Control blocker: ${approval.control_blockers[0]}`
                  : `Requested by ${approval.requested_by_name}; waiting on ${approval.current_approver_name || "an assigned approver"}.`}
              >
                <Stack direction="row" spacing={1.25} flexWrap="wrap" sx={{ mt: 0.75 }}>
                  {approval.amount !== null && <Typography variant="caption">Amount <strong>{controlMoney(approval.amount)}</strong></Typography>}
                  <Typography variant="caption">Requested {new Date(approval.requested_at).toLocaleDateString()}</Typography>
                  <Chip size="small" label={approval.legacy_compatibility ? "Legacy matrix" : `SoD · policy v${approval.policy_version ?? "?"}`} color={approval.legacy_compatibility ? "warning" : "success"} variant="outlined" sx={{ height: 20, fontSize: "0.68rem" }} />
                  <Chip size="small" label={`Control ${approval.control_status}`} color={approval.control_status === "blocked" ? "error" : "default"} variant="outlined" sx={{ height: 20, fontSize: "0.68rem" }} />
                  <Chip size="small" label={approval.blocks_lock ? "Blocks lock" : "Does not block this period"} variant="outlined" sx={{ height: 20, fontSize: "0.68rem" }} />
                </Stack>
                <ControlAction path={approval.action_path} label={approval.action_label} />
              </ControlRow>
            ))}
          </ControlSection>

          <ControlSection title="Agent-generated journals" subtitle="Journal provenance, status, amount, and approval state">
            {agentJournals.length === 0 ? (
              <ControlEmpty
                title="No agent-generated journals"
                detail="No journal in this period was drafted by a Ledger Core agent."
                path="/journals"
                action="Review all journals"
                success
              />
            ) : agentJournals.map((journal) => (
              <ControlRow
                key={journal.journal_id}
                title={`JE ${journal.journal_no} · ${journal.agent_name}`}
                status={journal.status === "draft" ? "break" : "pass"}
                detail={journal.description}
              >
                <Stack direction="row" spacing={1.25} flexWrap="wrap" sx={{ mt: 0.75 }}>
                  <Typography variant="caption">{journal.journal_date}</Typography>
                  <Typography variant="caption">Debits <strong>{controlMoney(journal.amount)}</strong></Typography>
                  {journal.approval_state && <Typography variant="caption">Approval <strong>{journal.approval_state}</strong></Typography>}
                </Stack>
                <ControlAction path={journal.action_path} label={journal.action_label} />
              </ControlRow>
            ))}
          </ControlSection>

          <ControlSection title="Material account movements" subtitle="Current net activity versus the trailing three-period average">
            {materialMovements.length === 0 ? (
              <ControlEmpty
                title="No material movements"
                detail="No posted account activity exceeded the deterministic $1,000 or 20% review threshold."
                path="/gl"
                action="Open general ledger"
                success
              />
            ) : materialMovements.map((movement) => (
              <ControlRow
                key={movement.account_id}
                title={`${movement.account_code} · ${movement.account_name}`}
                status={movement.severity}
                detail={`${movement.variance_pct === null ? "New activity" : `${Math.abs(movement.variance_pct).toFixed(1)}% variance`} versus the trailing average.`}
              >
                <Stack direction="row" spacing={1.25} flexWrap="wrap" sx={{ mt: 0.75 }}>
                  <Typography variant="caption">Current <strong>{controlMoney(movement.current_period_amount)}</strong></Typography>
                  <Typography variant="caption">Prior avg <strong>{controlMoney(movement.trailing_average_amount)}</strong></Typography>
                  <Typography variant="caption">Change <strong>{controlMoney(movement.difference)}</strong></Typography>
                  {movement.top_journal_no && <Typography variant="caption">Largest JE <strong>{movement.top_journal_no}</strong></Typography>}
                </Stack>
                <ControlAction path={movement.action_path} label={movement.action_label} />
              </ControlRow>
            ))}
          </ControlSection>

          <ControlSection title="Control overrides" subtitle="Who waived or forced a control, when, and why">
            {controlOverrides.length === 0 ? (
              <ControlEmpty
                title="No control overrides"
                detail="No checklist control was waived and no close preflight warning was forced for this period."
                path={`/close/${data.fiscal_period_id}`}
                action="Review close evidence"
                success
              />
            ) : controlOverrides.map((override) => (
              <ControlRow
                key={override.id}
                title={override.control}
                status="warning"
                detail={override.reason}
              >
                <Stack direction="row" spacing={1.25} flexWrap="wrap" sx={{ mt: 0.75 }}>
                  <Typography variant="caption">By <strong>{override.actor_name}</strong></Typography>
                  <Typography variant="caption">{new Date(override.occurred_at).toLocaleString()}</Typography>
                  <Chip size="small" label={override.override_type.replaceAll("_", " ")} variant="outlined" sx={{ height: 20, fontSize: "0.68rem" }} />
                </Stack>
                <ControlAction path={override.action_path} label={override.action_label} />
              </ControlRow>
            ))}
          </ControlSection>
        </Box>
      </CardContent>
    </Card>
  );
}

function ControlSection({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 2.5, overflow: "hidden", bgcolor: "background.paper" }}>
      <Box sx={{ px: 1.75, py: 1.25, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
      </Box>
      <Box sx={{ px: 1.75 }}>{children}</Box>
    </Box>
  );
}

function ControlRow({
  title,
  status,
  detail,
  children,
}: {
  title: string;
  status: string;
  detail: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ py: 1.4, borderBottom: 1, borderColor: "divider", "&:last-child": { borderBottom: 0 } }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={650}>{title}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.2 }}>{detail}</Typography>
        </Box>
        <Chip size="small" color={controlColor(status)} label={status.replace("_", " ")} sx={{ textTransform: "capitalize" }} />
      </Stack>
      {children}
    </Box>
  );
}

function ControlEmpty({
  title,
  detail,
  path,
  action,
  success = false,
}: {
  title: string;
  detail: string;
  path: string;
  action: string;
  success?: boolean;
}) {
  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="body2" fontWeight={650} color={success ? "success.main" : "text.primary"}>
        {success ? "✓ " : ""}{title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>{detail}</Typography>
      <ControlAction path={path} label={action} />
    </Box>
  );
}

// ─── Category card ────────────────────────────────────────────────────────

function CategoryCard({
  category,
  items,
  isClosed,
  onComplete,
  onStart,
  onWaive,
}: {
  category: string;
  items: CloseItem[];
  isClosed: boolean;
  onComplete: (id: string, note?: string) => void;
  onStart: (id: string) => void;
  onWaive: (item: CloseItem) => void;
}) {
  const done = items.filter((i) => i.status === "complete" || i.status === "waived").length;
  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={600}>
              {CATEGORY_LABEL[category] || category}
            </Typography>
            <Chip
              size="small"
              label={`${done}/${items.length}`}
              color={done === items.length ? "success" : "default"}
            />
          </Stack>
        }
        sx={{ pb: 0.5 }}
      />
      <CardContent sx={{ pt: 1 }}>
        <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
          {items.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              isClosed={isClosed}
              onComplete={onComplete}
              onStart={onStart}
              onWaive={onWaive}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─── Item row ─────────────────────────────────────────────────────────────

function ItemRow({
  item,
  isClosed,
  onComplete,
  onStart,
  onWaive,
}: {
  item: CloseItem;
  isClosed: boolean;
  onComplete: (id: string, note?: string) => void;
  onStart: (id: string) => void;
  onWaive: (item: CloseItem) => void;
}) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const isComplete = item.status === "complete";
  const isWaived = item.status === "waived";
  const isInProgress = item.status === "in_progress";
  const done = isComplete || isWaived;
  const isAutoVerified = isComplete && item.completion_note === "Auto-verified by system";
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = !done && item.due_date && item.due_date < today;

  return (
    <Stack id={`item-${item.id}`} direction="row" alignItems="center" spacing={1.5} sx={{ py: 1.25, scrollMarginTop: 16 }}>
      <Checkbox
        checked={isComplete}
        indeterminate={isWaived}
        disabled={isClosed || isWaived}
        onChange={() => !isComplete && onComplete(item.id)}
        size="small"
        sx={{ p: 0.5 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
          <Typography
            variant="body2"
            sx={{
              textDecoration: done ? "line-through" : "none",
              color: done ? "text.disabled" : "text.primary",
              fontWeight: 500,
            }}
          >
            {item.name}
          </Typography>
          {item.is_blocking && !done && (
            <Chip size="small" label="blocking" color="warning" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />
          )}
          {isInProgress && (
            <Chip size="small" label="in progress" color="primary" sx={{ height: 18, fontSize: "0.65rem" }} />
          )}
          {isWaived && (
            <Chip size="small" label="waived" color="warning" sx={{ height: 18, fontSize: "0.65rem" }} />
          )}
          {isAutoVerified && (
            <Tooltip title="Auto-verified by system">
              <VerifiedIcon sx={{ fontSize: 14, color: "success.main" }} />
            </Tooltip>
          )}
        </Stack>
        {item.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {item.description}
          </Typography>
        )}
        {item.completion_note && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic" }}>
            “{item.completion_note}”
          </Typography>
        )}
        {item.due_date && (
          <Typography
            variant="caption"
            color={isOverdue ? "error.main" : "text.secondary"}
            sx={{ display: "block", mt: 0.25 }}
          >
            Due {item.due_date}{isOverdue ? " · overdue" : ""}
          </Typography>
        )}
      </Box>
      {!isClosed && !done && (
        <>
          {!isInProgress && (
            <Button size="small" variant="text" onClick={() => onStart(item.id)}>
              Start
            </Button>
          )}
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                onComplete(item.id);
                setMenuAnchor(null);
              }}
            >
              Mark complete
            </MenuItem>
            <MenuItem
              onClick={() => {
                onWaive(item);
                setMenuAnchor(null);
              }}
            >
              Waive…
            </MenuItem>
          </Menu>
        </>
      )}
    </Stack>
  );
}

// ─── Status row ───────────────────────────────────────────────────────────

function StatusRow({
  label,
  value,
  color,
  highlight,
}: {
  label: string;
  value: number;
  color: "default" | "primary" | "warning" | "success" | "error";
  highlight?: boolean;
}) {
  const shouldEmphasize = highlight && value > 0;
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="body2"
        color={shouldEmphasize ? `${color}.main` : "text.secondary"}
        fontWeight={shouldEmphasize ? 600 : 400}
      >
        {label}
      </Typography>
      <Chip
        size="small"
        label={value}
        color={value > 0 ? color : "default"}
        variant={value > 0 && highlight ? "filled" : "outlined"}
      />
    </Stack>
  );
}

// ─── Dialogs ──────────────────────────────────────────────────────────────

function SignOffDialog({
  open,
  onClose,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
  isPending: boolean;
}) {
  const [note, setNote] = useState("");
  return (
    <Dialog open={open} onClose={() => !isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Sign off & close period</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This records controller sign-off and closes the fiscal period. The period remains correctable until the separate audit lock is applied after every live control passes. Your user ID and timestamp are recorded.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Sign-off note (optional)"
          placeholder="Any context worth recording in the audit trail…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          size="small"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<LockIcon />}
          disabled={isPending}
          onClick={() => onConfirm(note)}
        >
          {isPending ? "Closing…" : "Sign off & close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AdhocDialog({
  open,
  onClose,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: { name: string; category: string; description: string; is_blocking: boolean }) => void;
  isPending: boolean;
}) {
  const [form, setForm] = useState({
    name: "",
    category: "final",
    description: "",
    is_blocking: false,
  });
  return (
    <Dialog open={open} onClose={() => !isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Add ad-hoc checklist item</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Item name"
            placeholder="e.g. Confirm AWS February invoice posted"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            size="small"
            autoFocus
          />
          <TextField
            label="Category"
            select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            size="small"
          >
            {CATEGORY_ORDER.map((c) => (
              <MenuItem key={c} value={c}>{CATEGORY_LABEL[c]}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Description (optional)"
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            size="small"
          />
          <Stack direction="row" alignItems="center" spacing={1}>
            <Checkbox
              checked={form.is_blocking}
              onChange={(e) => setForm({ ...form, is_blocking: e.target.checked })}
              size="small"
              sx={{ p: 0.5 }}
            />
            <Typography variant="body2">Blocking — must complete before sign-off</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!form.name.trim() || isPending}
          onClick={() => onConfirm(form)}
        >
          {isPending ? "Adding…" : "Add item"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function WaiveDialog({
  item,
  onClose,
  onConfirm,
  isPending,
}: {
  item: CloseItem | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isPending: boolean;
}) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open={!!item} onClose={() => !isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Waive checklist item</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Waiving counts toward sign-off but is permanently recorded in the audit trail. Use this when an item legitimately doesn't apply this period (e.g. no fixed assets to depreciate).
        </Typography>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          {item?.name}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Reason (required)"
          placeholder="e.g. No fixed assets in service this month"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={!reason.trim() || isPending}
          onClick={() => onConfirm(reason)}
        >
          {isPending ? "Waiving…" : "Waive"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── AI Briefing card ────────────────────────────────────────────────────

function BriefingCard({
  briefing,
  isPending,
  error,
  onRun,
  period,
}: {
  briefing: AiBriefing | null;
  isPending: boolean;
  error: string | null;
  onRun: () => void;
  period: ClosePeriod;
}) {
  // Build an item lookup so we can render names/links from item_ids
  const itemMap = new Map<string, CloseItem>();
  for (const items of Object.values(period.items_by_category)) {
    for (const it of items) itemMap.set(it.id, it);
  }

  const statusColor: Record<AiBriefing["headline_status"], "default" | "primary" | "warning" | "error" | "success"> = {
    on_track: "primary",
    at_risk: "warning",
    blocking: "error",
    complete: "success",
  };

  return (
    <Card
      sx={{
        mb: 3,
        borderLeft: 4,
        borderColor: briefing ? `${statusColor[briefing.headline_status]}.main` : "divider",
      }}
    >
      <CardHeader
        avatar={<AutoAwesomeIcon sx={{ color: "primary.main" }} />}
        title="AI Briefing"
        subheader={
          briefing
            ? `Generated ${new Date(briefing.generated_at).toLocaleTimeString()} · ${briefing.model}`
            : "What to focus on to close on target. ~10s and a fraction of a cent in AI tokens."
        }
        action={
          <Button
            size="small"
            variant={briefing ? "outlined" : "contained"}
            startIcon={briefing ? <RefreshIcon /> : <AutoAwesomeIcon />}
            disabled={isPending}
            onClick={onRun}
          >
            {isPending ? "Thinking…" : briefing ? "Refresh" : "Run briefing"}
          </Button>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {error && <Alert severity="error">{error}</Alert>}

        {isPending && !briefing && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              AI is reviewing your checklist + close history…
            </Typography>
          </Stack>
        )}

        {!briefing && !isPending && !error && (
          <Typography variant="body2" color="text.secondary">
            No briefing yet — click <strong>Run briefing</strong> to triage today's work.
          </Typography>
        )}

        {briefing && (
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Chip
                size="small"
                color={statusColor[briefing.headline_status]}
                label={briefing.headline_status.replace("_", " ")}
                sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}
              />
              <Typography variant="body2">{briefing.summary}</Typography>
            </Stack>

            {briefing.critical_path.length > 0 && (
              <BriefingSection title="Critical path" emphasized>
                {briefing.critical_path.map((c) => {
                  const it = itemMap.get(c.item_id);
                  return (
                    <Stack key={c.item_id} direction="row" alignItems="flex-start" spacing={1} sx={{ py: 0.5 }}>
                      <Box sx={{ color: "warning.main", fontWeight: 700, fontSize: "0.8rem", pt: 0.25 }}>→</Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {it?.name || c.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {c.reason}
                        </Typography>
                        {c.owner_hint && (
                          <Typography variant="caption" color="primary.main" sx={{ display: "block", fontStyle: "italic" }}>
                            Suggested: {c.owner_hint}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  );
                })}
              </BriefingSection>
            )}

            {briefing.likely_waivable.length > 0 && (
              <BriefingSection title="Likely waivable" tip="Items the AI thinks don't apply this period">
                {briefing.likely_waivable.map((w) => {
                  const it = itemMap.get(w.item_id);
                  return (
                    <Stack key={w.item_id} direction="row" alignItems="flex-start" spacing={1} sx={{ py: 0.5 }}>
                      <Box sx={{ color: "text.disabled", fontWeight: 700, fontSize: "0.8rem", pt: 0.25 }}>○</Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={500}>{it?.name || w.title}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {w.reason}
                        </Typography>
                      </Box>
                    </Stack>
                  );
                })}
              </BriefingSection>
            )}

            {briefing.blocked_downstream.length > 0 && (
              <BriefingSection title="Dependencies" tip="Items waiting on something else to land first">
                {briefing.blocked_downstream.map((b, i) => {
                  const waiting = itemMap.get(b.waiting_on_item_id);
                  const blocks = itemMap.get(b.blocks_item_id);
                  return (
                    <Box key={i} sx={{ py: 0.5 }}>
                      <Typography variant="body2">
                        <strong>{blocks?.name}</strong> blocked by <strong>{waiting?.name}</strong>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{b.explanation}</Typography>
                    </Box>
                  );
                })}
              </BriefingSection>
            )}

            {briefing.pattern_insight && (
              <Alert severity="info" sx={{ mt: 0 }}>
                <Typography variant="body2">📊 {briefing.pattern_insight}</Typography>
              </Alert>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function BriefingSection({
  title,
  children,
  emphasized,
  tip,
}: {
  title: string;
  children: React.ReactNode;
  emphasized?: boolean;
  tip?: string;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: emphasized ? "warning.main" : "text.secondary",
          fontWeight: 600,
          display: "block",
          mb: 0.5,
        }}
      >
        {title}
        {tip && (
          <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1, textTransform: "none", letterSpacing: 0 }}>
            {tip}
          </Typography>
        )}
      </Typography>
      {children}
    </Box>
  );
}

// ─── AI Flux dialog ──────────────────────────────────────────────────────

function FluxDialog({
  open,
  onClose,
  data,
  isPending,
  error,
  onRefresh,
}: {
  open: boolean;
  onClose: () => void;
  data: AiFlux | null;
  isPending: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  const severityBorder = (s: FluxRow["severity"]) =>
    s === "critical" ? "error.main" : s === "warning" ? "warning.main" : "info.main";
  const directionIcon = (d: FluxRow["direction"]) =>
    d === "up" ? "↑" : d === "down" ? "↓" : "—";
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AutoAwesomeIcon sx={{ color: "primary.main" }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">AI Flux Analysis</Typography>
            <Typography variant="caption" color="text.secondary">
              Period vs trailing 3-month average · only material movements shown
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={isPending}
            onClick={onRefresh}
          >
            {isPending ? "Running…" : "Re-run"}
          </Button>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {isPending && !data && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4, justifyContent: "center" }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              AI is reviewing journal entries across all accounts…
            </Typography>
          </Stack>
        )}
        {data && (
          <Stack spacing={2}>
            <Alert severity="info" icon={<AutoAwesomeIcon />}>{data.summary}</Alert>
            {data.rows.length === 0 ? (
              <Alert severity="success">
                No material flux — all accounts within ±20% of trailing average.
              </Alert>
            ) : (
              <Stack spacing={1.25}>
                {data.rows.map((row) => (
                  <Box
                    key={row.account_id}
                    sx={{
                      border: 1, borderColor: "divider",
                      borderLeftWidth: 4, borderLeftColor: severityBorder(row.severity),
                      borderRadius: 1, p: 1.5,
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" spacing={2}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap">
                          <Typography variant="subtitle2" fontWeight={700}>
                            {row.account_code} · {row.account_name}
                          </Typography>
                          <Chip size="small" variant="outlined" label={row.account_type} sx={{ fontSize: "0.65rem", height: 18 }} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {row.explanation}
                        </Typography>
                        {row.action && (
                          <Typography variant="caption" color="text.primary" sx={{ display: "block", fontStyle: "italic", mt: 0.5 }}>
                            → {row.action}
                          </Typography>
                        )}
                        {row.top_journals.length > 0 && (
                          <Box sx={{ mt: 1, pl: 1, borderLeft: 2, borderColor: "divider" }}>
                            {row.top_journals.slice(0, 3).map((j) => (
                              <Typography key={j.journal_id} variant="caption" color="text.disabled" sx={{ display: "block", fontFamily: "monospace" }}>
                                JE-{j.journal_no} · {j.date} · {dollars(j.amount_cents)} · {j.description}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ textAlign: "right", flexShrink: 0, minWidth: 140 }}>
                        <Typography variant="h6" sx={{ color: severityBorder(row.severity), lineHeight: 1.1 }}>
                          {directionIcon(row.direction)} {dollars(Math.abs(row.delta_cents))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.variance_pct === null ? "new account" : `${row.variance_pct >= 0 ? "+" : ""}${row.variance_pct.toFixed(0)}% vs avg`}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ display: "block" }}>
                          now {dollars(row.current_period_cents)} / avg {dollars(row.trailing_avg_cents)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
            <Typography variant="caption" color="text.disabled" sx={{ textAlign: "right" }}>
              Generated {new Date(data.generated_at).toLocaleString()} · {data.model}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── AI Accrual Drafter dialog ───────────────────────────────────────────────

function AccrualsDialog({
  open,
  onClose,
  data,
  isPending,
  error,
  onRefresh,
}: {
  open: boolean;
  onClose: () => void;
  data: AiAccruals | null;
  isPending: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  const confidenceColor = (c: AiAccrualProposal["confidence"]) =>
    c === "high" ? "success" : c === "medium" ? "warning" : "default";
  const confidenceBorder = (c: AiAccrualProposal["confidence"]) =>
    c === "high" ? "success.main" : c === "medium" ? "warning.main" : "divider";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AutoAwesomeIcon sx={{ color: "primary.main" }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">AI Accrual Drafter</Typography>
            <Typography variant="caption" color="text.secondary">
              Recurring vendor bills not yet received · stale accrual balances
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={isPending}
            onClick={onRefresh}
          >
            {isPending ? "Running…" : "Re-run"}
          </Button>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {isPending && !data && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4, justifyContent: "center" }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              AI is scanning vendor billing patterns and accrual account balances…
            </Typography>
          </Stack>
        )}

        {data && (
          <Stack spacing={2.5}>
            <Alert severity="info" icon={<AutoAwesomeIcon />}>{data.summary}</Alert>

            {/* Proposed accruals */}
            <Box>
              <Typography
                variant="caption"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: "text.secondary", display: "block", mb: 1 }}
              >
                Proposed accruals
                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1, textTransform: "none", letterSpacing: 0 }}>
                  {data.vendor_count_checked} recurring vendor(s) checked
                </Typography>
              </Typography>

              {data.proposed_accruals.length === 0 ? (
                <Alert severity="success">
                  No missing recurring bills detected — all known vendors have invoices this period.
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {data.proposed_accruals.map((a) => (
                    <Box
                      key={a.id}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderLeftWidth: 4,
                        borderLeftColor: confidenceBorder(a.confidence),
                        borderRadius: 1,
                        p: 1.5,
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" spacing={2}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" mb={0.5}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {a.vendor_name}
                            </Typography>
                            <Chip
                              size="small"
                              label={a.confidence}
                              color={confidenceColor(a.confidence)}
                              variant="outlined"
                              sx={{ height: 18, fontSize: "0.65rem" }}
                            />
                          </Stack>
                          <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                            {a.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {a.reasoning}
                          </Typography>
                          {(a.debit_account_code || a.credit_account_code) && (
                            <Box
                              sx={{
                                mt: 1,
                                px: 1,
                                py: 0.5,
                                bgcolor: "grey.50",
                                borderRadius: 0.5,
                                fontFamily: "monospace",
                                fontSize: "0.75rem",
                                color: "text.secondary",
                              }}
                            >
                              Dr {a.debit_account_code ?? "?"} &nbsp;·&nbsp; Cr {a.credit_account_code ?? "?"}
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ textAlign: "right", flexShrink: 0, minWidth: 100 }}>
                          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                            {dollars(a.amount_cents)}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            avg monthly
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            {/* At-risk reversals */}
            {data.at_risk_reversals.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: "warning.main", display: "block", mb: 1 }}
                >
                  Stale accrual balances
                  <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1, textTransform: "none", letterSpacing: 0 }}>
                    {data.accrual_account_count} account(s) checked
                  </Typography>
                </Typography>
                <Stack spacing={1}>
                  {data.at_risk_reversals.map((r) => (
                    <Box
                      key={r.account_id}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderLeftWidth: 4,
                        borderLeftColor: "warning.main",
                        borderRadius: 1,
                        p: 1.5,
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" spacing={2}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {r.account_code} · {r.account_name}
                            </Typography>
                            <Chip
                              size="small"
                              label={`${r.oldest_entry_days}d old`}
                              color="warning"
                              variant="outlined"
                              sx={{ height: 18, fontSize: "0.65rem" }}
                            />
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                            → {r.suggestion}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right", flexShrink: 0, minWidth: 100 }}>
                          <Typography variant="h6" color="warning.main" sx={{ lineHeight: 1.2 }}>
                            {dollars(Math.abs(r.balance_cents))}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            net balance
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            <Typography variant="caption" color="text.disabled" sx={{ textAlign: "right" }}>
              Generated {new Date(data.generated_at).toLocaleString()} · {data.model}
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── AI Close-Package Narrative dialog ───────────────────────────────────────

function NarrativeDialog({
  open,
  onClose,
  data,
  isPending,
  error,
  onRefresh,
}: {
  open: boolean;
  onClose: () => void;
  data: AiNarrative | null;
  isPending: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  const handleCopy = () => {
    if (!data) return;
    const { memo } = data;
    const lines = [
      `MANAGEMENT CLOSE MEMO — ${memo.period_label}`,
      `Generated: ${new Date(data.generated_at).toLocaleString()} · ${data.model}`,
      "",
      memo.headline,
      "",
      ...memo.sections.flatMap((s) => [`${s.title.toUpperCase()}`, s.body, ""]),
      ...(memo.open_items.length > 0
        ? ["OPEN ITEMS:", ...memo.open_items.map((i) => `  • ${i}`)]
        : []),
    ];
    navigator.clipboard.writeText(lines.join("\n"));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AutoAwesomeIcon sx={{ color: "primary.main" }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">
              {data ? `Close Memo — ${data.memo.period_label}` : "AI Close-Package Memo"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Management memo · P&L · working capital · close process
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {data && (
              <Button size="small" variant="outlined" onClick={handleCopy}>
                Copy
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              disabled={isPending}
              onClick={onRefresh}
            >
              {isPending ? "Drafting…" : "Re-draft"}
            </Button>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {isPending && !data && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4, justifyContent: "center" }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              AI is drafting the close memo from P&L, AR/AP, and checklist data…
            </Typography>
          </Stack>
        )}

        {data && (
          <Stack spacing={2.5}>
            {/* Headline */}
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 1,
                borderLeft: 4,
                borderColor: data.memo.close_status === "closed" ? "success.main" : "primary.main",
              }}
            >
              <Typography variant="body1" fontWeight={600}>
                {data.memo.headline}
              </Typography>
            </Box>

            {/* Open items warning (shown before sections if any) */}
            {data.memo.open_items.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Open items as of memo date:
                </Typography>
                {data.memo.open_items.map((item, i) => (
                  <Typography key={i} variant="body2">• {item}</Typography>
                ))}
              </Alert>
            )}

            {/* Memo sections */}
            {data.memo.sections.map((section, i) => (
              <Box key={i}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    fontWeight: 700,
                    color: "text.secondary",
                    display: "block",
                    mb: 0.75,
                    borderBottom: 1,
                    borderColor: "divider",
                    pb: 0.5,
                  }}
                >
                  {section.title}
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                  {section.body}
                </Typography>
              </Box>
            ))}

            <Typography variant="caption" color="text.disabled" sx={{ textAlign: "right" }}>
              Generated {new Date(data.generated_at).toLocaleString()} · {data.model}
            </Typography>
          </Stack>
        )}

        {!data && !isPending && !error && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Click <strong>Re-draft</strong> to generate the management close memo for this period.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        {data && (
          <Button onClick={handleCopy} sx={{ mr: "auto" }}>
            Copy to clipboard
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── AI Bad-Debt Estimator dialog ────────────────────────────────────────────

function BadDebtDialog({
  open,
  onClose,
  data,
  isPending,
  error,
  onRefresh,
}: {
  open: boolean;
  onClose: () => void;
  data: AiBadDebt | null;
  isPending: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  const confidenceColor = (c: AiBadDebtExposure["confidence"]) =>
    c === "high" ? "error" : c === "medium" ? "warning" : "default";
  const confidenceBorder = (c: AiBadDebtExposure["confidence"]) =>
    c === "high" ? "error.main" : c === "medium" ? "warning.main" : "divider";

  const agingBuckets = data
    ? [
        { label: "Current", cents: data.aging_summary.current_cents },
        { label: "31-60 DPD", cents: data.aging_summary.days_31_60_cents },
        { label: "61-90 DPD", cents: data.aging_summary.days_61_90_cents },
        { label: "91-120 DPD", cents: data.aging_summary.days_91_120_cents },
        { label: "120+ DPD", cents: data.aging_summary.days_120_plus_cents },
      ]
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AutoAwesomeIcon sx={{ color: "primary.main" }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">AI Bad-Debt Estimator</Typography>
            <Typography variant="caption" color="text.secondary">
              AR aging analysis · per-customer reserve recommendations
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={isPending}
            onClick={onRefresh}
          >
            {isPending ? "Running…" : "Re-run"}
          </Button>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {isPending && !data && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4, justifyContent: "center" }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              AI is analyzing AR aging buckets and write-off history…
            </Typography>
          </Stack>
        )}

        {data && (
          <Stack spacing={2.5}>
            <Alert severity="info" icon={<AutoAwesomeIcon />}>{data.summary}</Alert>

            {/* Aging summary strip */}
            <Box>
              <Typography
                variant="caption"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: "text.secondary", display: "block", mb: 1 }}
              >
                Aging summary
                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1, textTransform: "none", letterSpacing: 0 }}>
                  {data.customers_checked} customer(s) · total AR {dollars(data.total_ar_cents)}
                </Typography>
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {agingBuckets.map((b) => (
                  <Box
                    key={b.label}
                    sx={{
                      flex: "1 1 auto",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1,
                      textAlign: "center",
                      minWidth: 90,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {b.label}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={700} color={b.cents > 0 ? "text.primary" : "text.disabled"}>
                      {dollars(b.cents)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Per-customer reserve recommendations */}
            <Box>
              <Typography
                variant="caption"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: "text.secondary", display: "block", mb: 1 }}
              >
                Reserve recommendations
                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1, textTransform: "none", letterSpacing: 0 }}>
                  suggested total: {dollars(data.suggested_total_reserve_cents)}
                </Typography>
              </Typography>

              {data.customer_exposures.length === 0 ? (
                <Alert severity="success">No customers with open AR found.</Alert>
              ) : (
                <Stack spacing={1}>
                  {data.customer_exposures.map((exp) => (
                    <Box
                      key={exp.customer_id}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderLeftWidth: 4,
                        borderLeftColor: confidenceBorder(exp.confidence),
                        borderRadius: 1,
                        p: 1.5,
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" spacing={2}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" mb={0.5}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {exp.customer_name}
                            </Typography>
                            <Chip
                              size="small"
                              label={exp.confidence}
                              color={confidenceColor(exp.confidence)}
                              variant="outlined"
                              sx={{ height: 18, fontSize: "0.65rem" }}
                            />
                            {exp.invoice_count > 1 && (
                              <Typography variant="caption" color="text.disabled">
                                {exp.invoice_count} invoices
                              </Typography>
                            )}
                          </Stack>
                          {/* Aging mini-bar */}
                          <Stack direction="row" spacing={0.5} sx={{ mb: 0.75 }} flexWrap="wrap">
                            {[
                              { label: "Current", v: exp.current_cents },
                              { label: "31-60", v: exp.days_31_60_cents },
                              { label: "61-90", v: exp.days_61_90_cents },
                              { label: "91-120", v: exp.days_91_120_cents },
                              { label: "120+", v: exp.days_120_plus_cents },
                            ]
                              .filter((b) => b.v > 0)
                              .map((b) => (
                                <Chip
                                  key={b.label}
                                  size="small"
                                  label={`${b.label}: ${dollars(b.v)}`}
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: "0.65rem" }}
                                />
                              ))}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {exp.reasoning}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right", flexShrink: 0, minWidth: 110 }}>
                          <Typography variant="caption" color="text.disabled" sx={{ display: "block" }}>
                            outstanding
                          </Typography>
                          <Typography variant="subtitle2">
                            {dollars(exp.total_outstanding_cents)}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>
                            suggested reserve
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ lineHeight: 1.2, color: confidenceBorder(exp.confidence) }}
                          >
                            {dollars(exp.recommended_reserve_cents)}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {exp.total_outstanding_cents > 0
                              ? `${((exp.recommended_reserve_cents / exp.total_outstanding_cents) * 100).toFixed(1)}% of AR`
                              : ""}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <Typography variant="caption" color="text.disabled" sx={{ textAlign: "right" }}>
              Generated {new Date(data.generated_at).toLocaleString()} · {data.model}
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function dollars(cents: number): string {
  const abs = Math.abs(cents);
  if (abs >= 1_000_000_00) return `$${(cents / 1_000_000_00).toFixed(1)}M`;
  if (abs >= 1_000_00) return `$${(cents / 1_000_00).toFixed(1)}k`;
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}
